/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * User Story Operations
 * 
 * Handles user story grooming, epic grooming, and status updates.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { getProductServerBaseUrl } from './base';

export type UserStoryResult = { success: boolean; result?: string; error?: string; executionId?: string };

/**
 * Groom a user story to break it down into implementable issues
 * Grooming operates directly on the velocity branch (main worktree) since it only
 * modifies product data in data/product/. Worktrees are not needed for grooming.
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function groomUserStory(story: any, activeProduct?: any): Promise<UserStoryResult> {
  try {
    const promptRequest = {
      promptName: 'Groom User Story',
      params: {
        storyData: JSON.stringify(story),
        request: `Please groom the provided user story by breaking it down into specific, implementable issues (bugs, tasks, features). Create detailed issues in issues.md that can be assigned to developers.`,
        activeProduct: activeProduct || null,
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'grooming' as const,
        entityType: 'story' as const,
        entityId: story.id,
        productId: activeProduct?.id,
      },
    };

    console.log('[SkillShop Service] Grooming user story:', { storyId: story.id });

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;

    console.log('[SkillShop Service] User story grooming completed successfully');
    return {
      success: true,
      result: result,
      executionId,
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error grooming user story:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to groom user story'
    };
  }
}

/**
 * Groom an entire epic to break it down into implementable issues
 * Grooming operates directly on the velocity branch (main worktree) since it only
 * modifies product data in data/product/. Worktrees are not needed for grooming.
 * SkillShop server endpoint - uses authenticatedFetch
 */
export async function groomEpic(epic: any, stories: any[], activeProduct?: any): Promise<UserStoryResult> {
  try {
    const promptRequest = {
      promptName: 'Groom Epic',
      params: {
        epicData: JSON.stringify(epic),
        storiesData: JSON.stringify(stories),
        request: `Please groom the provided epic by analyzing all its user stories and breaking them down into specific, implementable issues (bugs, tasks, features). Create detailed issues in issues.md that can be assigned to developers. Look for shared infrastructure needs and organize issues efficiently across all stories.`,
        activeProduct: activeProduct || null,
      },
      returnRawText: true,
      velocityRunContext: {
        type: 'grooming' as const,
        entityType: 'epic' as const,
        entityId: epic.id,
        productId: activeProduct?.id,
      },
    };

    console.log('[SkillShop Service] Grooming epic:', { epicId: epic.id, storyCount: stories.length });

    const response = await authenticatedFetch('/api/llm/prompt', {
      method: 'POST',
      body: promptRequest,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API request failed (${response.status}): ${errorText}`);
    }

    const result = await response.text();
    const executionId = response.headers.get('X-Agent-Execution-Id') ?? undefined;

    console.log('[SkillShop Service] Epic grooming completed successfully');
    return {
      success: true,
      result: result,
      executionId,
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error grooming epic:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to groom epic'
    };
  }
}

/**
 * Update a user story's status
 * Product server endpoint - uses direct fetch with repo context
 */
export async function updateUserStoryStatus(
  storyId: string, 
  newStatus: string, 
  repoUrl?: string, 
  repoPath?: string
): Promise<UserStoryResult> {
  try {
    console.log('[SkillShop Service] Updating user story status:', { storyId, newStatus, repoUrl, repoPath });

    // Build URL with repo context query parameters
    const baseUrl = `${getProductServerBaseUrl()}/api/data/user-stories/${encodeURIComponent(storyId)}/status`;
    const params = new URLSearchParams();
    if (repoUrl) params.set('repoUrl', repoUrl);
    if (repoPath) params.set('repoPath', repoPath);
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status: newStatus }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Status update failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    
    console.log('[SkillShop Service] User story status updated successfully:', result);
    return {
      success: true,
      result: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error updating user story status:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to update user story status'
    };
  }
}

/**
 * Update user story fields
 * Product server endpoint - uses direct fetch with repo context
 */
export async function updateUserStoryFields(
  storyId: string, 
  fields: any, 
  repoUrl?: string, 
  repoPath?: string
): Promise<UserStoryResult> {
  try {
    console.log('[SkillShop Service] Updating user story fields:', { storyId, repoUrl, repoPath });

    // Build URL with repo context query parameters
    const baseUrl = `${getProductServerBaseUrl()}/api/data/user-stories/${encodeURIComponent(storyId)}`;
    const params = new URLSearchParams();
    if (repoUrl) params.set('repoUrl', repoUrl);
    if (repoPath) params.set('repoPath', repoPath);
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fields),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Field update failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    
    console.log('[SkillShop Service] User story fields updated successfully:', result);
    return {
      success: true,
      result: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error updating user story fields:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to update user story fields'
    };
  }
}

/**
 * Delete a user story from markdown storage
 * Product server endpoint - uses direct fetch with repo context
 */
export async function deleteUserStory(
  storyId: string,
  repoUrl?: string,
  repoPath?: string
): Promise<UserStoryResult> {
  try {
    console.log('[SkillShop Service] Deleting user story:', { storyId, repoUrl, repoPath });

    const baseUrl = `${getProductServerBaseUrl()}/api/data/user-stories/${encodeURIComponent(storyId)}`;
    const params = new URLSearchParams();
    if (repoUrl) params.set('repoUrl', repoUrl);
    if (repoPath) params.set('repoPath', repoPath);
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Delete failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();

    console.log('[SkillShop Service] User story deleted successfully:', result);
    return {
      success: true,
      result: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error deleting user story:', error);

    return {
      success: false,
      error: error.message || 'Failed to delete user story'
    };
  }
}

/**
 * Update epic fields
 * Product server endpoint - uses direct fetch with repo context
 */
export async function updateEpicFields(
  epicId: string, 
  fields: any, 
  repoUrl?: string, 
  repoPath?: string
): Promise<UserStoryResult> {
  try {
    console.log('[SkillShop Service] Updating epic fields:', { epicId, repoUrl, repoPath });

    // Build URL with repo context query parameters
    const baseUrl = `${getProductServerBaseUrl()}/api/data/epics/${encodeURIComponent(epicId)}`;
    const params = new URLSearchParams();
    if (repoUrl) params.set('repoUrl', repoUrl);
    if (repoPath) params.set('repoPath', repoPath);
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fields),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Epic field update failed (${response.status}): ${errorText}`);
    }

    const result = await response.json();
    
    console.log('[SkillShop Service] Epic fields updated successfully:', result);
    return {
      success: true,
      result: result.message
    };
  } catch (error: any) {
    console.error('[SkillShop Service] Error updating epic fields:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to update epic fields'
    };
  }
}

